import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { procurementApi, type QuotationComparisonRow } from '@/api/procurement.api';
import { useAppSelector } from '@/hooks/useAppSelector';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { Input } from '@/components/ui/Input';
import { RfqSelect, SupplierSelect } from '@/components/selects';
import {
  createProductLineDraft,
  parseProductLines,
  ProductLineItemsEditor,
  type ProductLineDraft,
} from '@/components/forms/ProductLineItemsEditor';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { OperationsLayout } from '@/shared/layouts/OperationsLayout';
import { usePermissions } from '@/hooks/usePermissions';
import { formatCurrency, formatDate } from '@/utils/format';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function QuotationComparisonPage() {
  const { hasAny } = usePermissions();
  const canManageRfq = hasAny('PROCUREMENT_RFQ_MANAGE', 'PROCUREMENT_MANAGE');
  const canCreatePo = hasAny('PROCUREMENT_PO_CREATE', 'PROCUREMENT_MANAGE');
  const store = useAppSelector((s) => s.app.selectedStore);
  const [rfqId, setRfqId] = useState('');
  const [rows, setRows] = useState<QuotationComparisonRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const { rows: paginatedRows, tablePaginationProps: rowsPaginationProps } = useClientPaginatedRows(rows);

  const [quoteSupplierId, setQuoteSupplierId] = useState('');
  const [quoteDelivery, setQuoteDelivery] = useState('');
  const [quotePaymentTerms, setQuotePaymentTerms] = useState('Net 30');
  const [quoteLines, setQuoteLines] = useState<ProductLineDraft[]>([
    createProductLineDraft({ quantity: '1', unitPrice: '0' }),
  ]);
  const [recording, setRecording] = useState(false);

  const loadComparison = useCallback(async (selectedRfqId = rfqId) => {
    const parsed = Number(selectedRfqId);
    if (!parsed) {
      setRows([]);
      setHasLoaded(false);
      return;
    }
    setLoading(true);
    try {
      const data = await procurementApi.compareQuotations(parsed);
      setRows(data);
      setHasLoaded(true);
    } catch (err) {
      showApiError(err, 'Failed to load quotation comparison');
    } finally {
      setLoading(false);
    }
  }, [rfqId]);

  useEffect(() => {
    void loadComparison(rfqId);
  }, [rfqId, loadComparison]);

  const recordQuotation = async () => {
    const parsedRfqId = Number(rfqId);
    const parsedSupplierId = Number(quoteSupplierId);
    if (!parsedRfqId) {
      notify.error('Select an RFQ first');
      return;
    }
    if (!parsedSupplierId) {
      notify.error('Select a supplier');
      return;
    }
    const itemsPayload = parseProductLines(quoteLines, 'price');
    if (itemsPayload.length === 0) {
      notify.error('Add at least one quoted product line with unit price');
      return;
    }
    setRecording(true);
    try {
      await procurementApi.recordQuotation({
        rfqId: parsedRfqId,
        supplierId: parsedSupplierId,
        deliveryDate: quoteDelivery || undefined,
        paymentTerms: quotePaymentTerms.trim() || undefined,
        items: itemsPayload,
      });
      notify.success('Quotation recorded');
      setQuoteSupplierId('');
      setQuoteDelivery('');
      setQuoteLines([createProductLineDraft({ quantity: '1', unitPrice: '0' })]);
      await loadComparison();
    } catch (err) {
      showApiError(err, 'Record quotation failed');
    } finally {
      setRecording(false);
    }
  };

  const createPoFromQuote = async (row: QuotationComparisonRow) => {
    if (!store) {
      notify.error('Select a store first');
      return;
    }
    try {
      const po = await procurementApi.createPurchaseOrder({
        storeId: store.id,
        supplierId: row.supplierId,
        quotationId: row.quotationId,
        items: [],
      });
      notify.success(`PO ${po.poNumber} created — submit for approval`);
    } catch (err) {
      showApiError(err, 'Create PO failed');
    }
  };

  const selectWinner = async (id: number) => {
    try {
      await procurementApi.selectQuotation(id);
      notify.success('Winning quotation selected');
      await loadComparison();
    } catch (err) {
      showApiError(err, 'Failed to select quotation');
    }
  };

  const emptyMessage = !rfqId
    ? 'Select an RFQ to compare supplier quotations.'
    : hasLoaded && rows.length === 0
      ? 'No quotations yet for this RFQ. Record quotes below or ask suppliers to submit via the vendor portal.'
      : 'Loading comparison…';

  return (
    <OperationsLayout
      moduleLabel="Procurement"
      title="Quotation Comparison"
      description="Compare supplier quotations by price, delivery and terms before selection."
    >
      <div className="card mb-4 space-y-3 p-4">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Comparison rows appear only after at least one supplier quotation exists for the RFQ. Suppliers invited on the
          RFQ can quote at{' '}
          <Link to="/vendor-portal/login" className="text-primary-600 hover:underline">
            Vendor portal
          </Link>
          , or procurement can record quotes manually below.
        </p>
        <div className="flex flex-wrap gap-2">
          <RfqSelect label="RFQ *" value={rfqId} onChange={setRfqId} required className="min-w-[240px] flex-1" />
          <Button className="self-end" variant="secondary" loading={loading} disabled={!rfqId} onClick={() => void loadComparison()}>
            Refresh
          </Button>
        </div>
      </div>

      {canManageRfq && rfqId && (
        <section className="card mb-4 space-y-3 p-4">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Record supplier quotation</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Use this when a supplier sends pricing offline, or for testing before vendor portal access is set up.
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            <SupplierSelect label="Supplier *" value={quoteSupplierId} onChange={setQuoteSupplierId} required />
            <DatePicker label="Delivery date" value={quoteDelivery} onChange={setQuoteDelivery} />
            <Input label="Payment terms" value={quotePaymentTerms} onChange={(e) => setQuotePaymentTerms(e.target.value)} />
          </div>
          <ProductLineItemsEditor lines={quoteLines} onChange={setQuoteLines} priceMode="price" />
          <Button loading={recording} onClick={() => void recordQuotation()}>
            Record quotation
          </Button>
        </section>
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left dark:border-slate-700">
              <th className="p-3">Supplier</th>
              <th className="p-3">Total</th>
              <th className="p-3">Delivery</th>
              <th className="p-3">Terms</th>
              <th className="p-3">Highlights</th>
              <th className="p-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-4 text-slate-500">
                  {loading ? 'Loading…' : emptyMessage}
                </td>
              </tr>
            ) : (
              paginatedRows.map((row) => (
                <tr key={row.quotationId} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="p-3 font-medium">{row.supplierName}</td>
                  <td className="p-3">{formatCurrency(row.totalAmount)}</td>
                  <td className="p-3">{row.deliveryDate ? formatDate(row.deliveryDate) : '—'}</td>
                  <td className="p-3">{row.paymentTerms ?? '—'}</td>
                  <td className="p-3">
                    {row.bestPrice ? 'Best price ' : ''}
                    {row.fastestDelivery ? 'Fastest delivery' : ''}
                    {!row.bestPrice && !row.fastestDelivery ? '—' : ''}
                  </td>
                  <td className="p-3">
                    {row.selected ? (
                      <div className="flex flex-col gap-1">
                        <span className="text-emerald-600">Selected</span>
                        {canCreatePo && (
                          <Button size="sm" variant="secondary" onClick={() => void createPoFromQuote(row)}>
                            Create PO
                          </Button>
                        )}
                        <Link to="/procurement/purchase-orders" className="text-xs text-primary-600 hover:underline">
                          View POs
                        </Link>
                      </div>
                    ) : (
                      canManageRfq && (
                        <Button onClick={() => void selectWinner(row.quotationId)}>Select</Button>
                      )
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <TablePagination {...rowsPaginationProps} hasRows={rows.length > 0} loading={loading} />
      </div>
    </OperationsLayout>
  );
}
